import { useState, useEffect } from 'react';
import { useRoute, useLocation, Link } from 'wouter';
import { useRequireAdminAuth } from '../../hooks/useAdminAuth';
import AdminLayout from '../../components/admin/AdminLayout';
import ConversationTranscript from '../../components/admin/ConversationTranscript';
import ParticipantInfoPanel from '../../components/admin/ParticipantInfoPanel';
import { Button } from '../../components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  getConversation,
  exportData,
  downloadBlob,
  getChatLogSource,
  deleteConversation,
} from '../../lib/admin-api';
import type { ChatLog } from '../../types/admin';
import {
  ArrowLeft,
  Download,
  Loader2,
  AlertCircle,
  Trash2,
} from 'lucide-react';
import { Alert, AlertDescription } from '../../components/ui/alert';

export default function ConversationView() {
  const { isLoading: authLoading, isAuthenticated } = useRequireAdminAuth();
  const [, params] = useRoute('/admin/conversation/:patientId/:sessionId');

  const [conversation, setConversation] = useState<ChatLog | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [, setLocation] = useLocation();
  const [canDelete, setCanDelete] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const patientId = params?.patientId ? decodeURIComponent(params.patientId) : null;
  const sessionId = params?.sessionId ? decodeURIComponent(params.sessionId) : null;

  useEffect(() => {
    const fetchConversation = async () => {
      if (!patientId || !sessionId || !isAuthenticated) return;

      setIsLoading(true);
      setError(null);

      try {
        const data = await getConversation(patientId, sessionId);
        setConversation(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load conversation');
      } finally {
        setIsLoading(false);
      }
    };

    fetchConversation();
  }, [patientId, sessionId, isAuthenticated]);

  // Deletion is only offered for the database source
  useEffect(() => {
    if (!isAuthenticated) return;
    getChatLogSource()
      .then((status) => setCanDelete(status.source === 'database'))
      .catch(() => setCanDelete(false));
  }, [isAuthenticated]);

  const handleDelete = async () => {
    if (!patientId || !sessionId) return;
    setIsDeleting(true);
    try {
      await deleteConversation(patientId, sessionId);
      setLocation('/admin');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
      setIsDeleting(false);
      setConfirmDeleteOpen(false);
    }
  };

  const handleExport = async () => {
    if (!patientId) return;

    setIsExporting(true);
    try {
      const blob = await exportData('csv', patientId);
      const filename = `conversation-${patientId.substring(0, 8)}-${sessionId?.substring(0, 8)}.csv`;
      downloadBlob(blob, filename);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setIsExporting(false);
    }
  };

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <div className="mt-4">
          <Link href="/admin">
            <Button variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
          </Link>
        </div>
      </AdminLayout>
    );
  }

  if (!conversation) {
    return (
      <AdminLayout>
        <div className="text-center py-12 text-gray-500">
          Conversation not found
        </div>
        <div className="text-center mt-4">
          <Link href="/admin">
            <Button variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
          </Link>
        </div>
      </AdminLayout>
    );
  }

  const patientName =
    conversation.patientFirstName || conversation.patientLastName
      ? `${conversation.patientFirstName || ''} ${conversation.patientLastName || ''}`.trim()
      : 'Patient';

  return (
    <AdminLayout>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 space-y-4 sm:space-y-0">
        <div className="flex items-center space-x-4">
          <Link href="/admin">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">AI Chat</h1>
            <p className="text-sm text-gray-500">
              Conversation with {patientName}
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={isExporting}
            title="Export conversation to CSV"
          >
            {isExporting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            Export CSV
          </Button>
          {canDelete && (
            <Button
              variant="outline"
              size="sm"
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={() => setConfirmDeleteOpen(true)}
              disabled={isDeleting}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          )}
        </div>
      </div>

      <AlertDialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this conversation?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the conversation and all of its messages from the application
              database. Export first if you need a copy. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              disabled={isDeleting}
              onClick={(e) => {
                e.preventDefault();
                handleDelete();
              }}
            >
              {isDeleting ? 'Deleting…' : 'Delete conversation'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Main content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Transcript */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg border border-gray-200 p-6 max-h-[calc(100vh-250px)] overflow-y-auto">
            <ConversationTranscript messages={conversation.messages} />
          </div>
        </div>

        {/* Participant Info */}
        <div className="lg:col-span-1">
          <ParticipantInfoPanel conversation={conversation} />
        </div>
      </div>
    </AdminLayout>
  );
}
